// Public /pricing page — Tracker is the anchor product every church buys; the
// three add-ons layer on top. Add-on pricing scales with the Tracker tier
// (Tier 1 → $5 each, Tier 2 → $10 each, Tier 3 → $20 each, Tier 4 → $40 each).

import { Link } from "react-router-dom";
import {
  BookOpen, Users, Heart, Tablet, ArrowRight, CheckCircle2, Plus,
} from "lucide-react";

const TIERS = [
  { key: "tier_1", label: "Tier 1", range: "1 – 50 directory profiles",     tracker: 19, addon: 4.99 },
  { key: "tier_2", label: "Tier 2", range: "51 – 250 directory profiles",   tracker: 29, addon: 9.99 },
  { key: "tier_3", label: "Tier 3", range: "251 – 1,000 directory profiles",tracker: 39, addon: 14.99 },
  { key: "tier_4", label: "Tier 4", range: "1,001 – 2,000 directory profiles", tracker: 59, addon: 19.99 },
];

const ADDONS = [
  {
    key: "attendance", icon: Users, label: "Attendance",
    blurb: "Church-wide services, sections, YTD reports, QR check-ins.",
    accent: "var(--olive)",
  },
  {
    key: "prayer", icon: Heart, label: "Prayer Log",
    blurb: "Public prayer visit log — celebrate your church's prayer habit.",
    accent: "var(--terracotta)",
  },
  {
    key: "kiosk", icon: Tablet, label: "Kiosk",
    blurb: "Tablet-first lobby experience combining check-in, prayer, and sign-up.",
    accent: "var(--gold)",
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-10 md:py-20">
      <header className="max-w-3xl mb-10 md:mb-16">
        <div className="overline mb-3">Pricing</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl tracking-[-0.03em] leading-[0.95]" style={{ color: "var(--ink)" }}>
          Start with the <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>Tracker</span>.<br />
          Add tools as you grow.
        </h1>
        <p className="mt-4 md:mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Every church starts on the Bible Study Tracker. Layer on Attendance, Prayer Log, or Kiosk any time — add-on pricing scales with your Tracker tier. Monthly, cancel anytime.
        </p>
        <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
          <a href="mailto:thedisciplelifems@gmail.com?subject=Book%20a%20demo%20-%20The%20Disciple%20Life" className="btn-primary" data-testid="pricing-demo-btn">
            Book a demo <ArrowRight size={14} />
          </a>
          <Link to="/membership" className="btn-ghost" data-testid="pricing-membership-btn">
            Individual membership
          </Link>
        </div>
      </header>

      {/* Step 1 — Tracker base */}
      <section className="mb-16" data-testid="pricing-tracker-section">
        <div className="flex items-baseline gap-3 mb-6">
          <div className="overline">Step 1 · Required base</div>
          <span className="text-[0.65rem] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full" style={{ background: "var(--ember)", color: "var(--bg-base)" }}>Included</span>
        </div>
        <div className="p-6 md:p-8 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-start gap-3 mb-6">
            <BookOpen size={22} style={{ color: "var(--ember)", marginTop: 4 }} />
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--ink)" }}>Bible Study Tracker</h2>
              <p className="text-sm md:text-base mt-1 max-w-2xl" style={{ color: "var(--ink-2)" }}>
                Guest pipeline (Introduction → Home Study → Next Steps), teacher assignments, hospitality connection log, guest directory. This is the base every plan includes.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pricing-tracker-tiers">
            {TIERS.map((t) => (
              <div key={t.key} className="p-4 rounded-md" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }} data-testid={`pricing-tracker-${t.key}`}>
                <div className="overline mb-1">{t.label}</div>
                <div className="font-serif-display text-3xl" style={{ color: "var(--ember)" }}>${t.tracker}<span className="text-sm" style={{ color: "var(--muted)" }}> /mo</span></div>
                <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{t.range}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 2 — Add-ons */}
      <section className="mb-16" data-testid="pricing-addon-section">
        <div className="flex items-baseline gap-3 mb-6">
          <div className="overline">Step 2 · Optional add-ons</div>
          <span className="text-xs" style={{ color: "var(--muted)" }}>Pick any, in any combo</span>
        </div>
        <p className="text-sm md:text-base mb-6 max-w-2xl" style={{ color: "var(--ink-2)" }}>
          Each add-on's monthly price is set by your Tracker tier. A church on Tier 2 Tracker adds any tool for $9.99/mo, for example.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-addon-cards">
          {ADDONS.map((tool) => {
            const Icon = tool.icon;
            return (
              <div key={tool.key} className="p-5 md:p-6 rounded-md relative" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`pricing-addon-${tool.key}`}>
                <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full flex items-center justify-center" style={{ background: tool.accent, color: "var(--bg-base)" }}>
                  <Plus size={14} />
                </div>
                <div className="flex items-center gap-2 mb-2 mt-1">
                  <Icon size={18} style={{ color: tool.accent }} />
                  <h3 className="font-serif-display text-xl md:text-2xl" style={{ color: "var(--ink)" }}>{tool.label}</h3>
                </div>
                <p className="text-xs md:text-sm mb-4" style={{ color: "var(--muted)" }}>{tool.blurb}</p>
                <ul className="space-y-1.5">
                  {TIERS.map((t) => (
                    <li key={t.key} className="flex items-center justify-between text-sm">
                      <span style={{ color: "var(--ink-2)" }}>{t.label}</span>
                      <span className="font-serif-display" style={{ color: tool.accent }}>+${t.addon}<span className="text-xs" style={{ color: "var(--muted)" }}> /mo</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Example combos */}
      <section className="mb-14" data-testid="pricing-examples">
        <div className="overline mb-4">A few example totals</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: "Tier 1 · Tracker only", price: "19", note: "Starter church, guest pipeline only." },
            { title: "Tier 2 · Tracker + Attendance", price: "38.99", note: "$29 Tracker + $9.99 Attendance." },
            { title: "Tier 3 · All four tools", price: "83.97", note: "$39 Tracker + 3× $14.99 add-ons." },
          ].map((ex) => (
            <div key={ex.title} className="p-5 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
              <div className="text-xs uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>{ex.title}</div>
              <div className="font-serif-display text-3xl mt-1" style={{ color: "var(--ink)" }}>${ex.price}<span className="text-sm" style={{ color: "var(--muted)" }}> /mo</span></div>
              <div className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>{ex.note}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
        {[
          { title: "One card, one bill", body: "Every tool rolls into a single monthly Stripe charge. Cancel or change tools anytime — proration handled automatically." },
          { title: "No user seat limits", body: "Coordinators, teachers, section leaders, and org admins are unlimited on every tier. The tier only sizes your directory." },
          { title: "Free to try", body: "Message us for a demo, and we'll comp your first month while you and your team kick the tires." },
        ].map((f) => (
          <div key={f.title} className="p-5 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
            <CheckCircle2 size={16} style={{ color: "var(--olive)" }} />
            <h3 className="font-serif-display text-lg mt-2" style={{ color: "var(--ink)" }}>{f.title}</h3>
            <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>{f.body}</p>
          </div>
        ))}
      </section>

      <div className="p-6 md:p-10 rounded-md text-center" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="pricing-cta-block">
        <h2 className="font-serif-display text-2xl md:text-4xl" style={{ color: "var(--ink)" }}>See it before you subscribe.</h2>
        <p className="mt-2 text-sm md:text-base max-w-xl mx-auto" style={{ color: "var(--ink-2)" }}>
          Book a 20-minute demo — we'll spin up your church, walk you through each tool, and comp your first month so you can decide with real data.
        </p>
        <a href="mailto:thedisciplelifems@gmail.com?subject=Book%20a%20demo%20-%20The%20Disciple%20Life" className="btn-primary mt-4 md:mt-6" data-testid="pricing-cta-demo-btn">
          Book a demo <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}
